import {
  ArrowRight,
  HeartHandshake,
  Sparkles,
} from "lucide-react";

import type { HouseholdIntelligenceResponse } from "@/api/householdIntelligence";
import { Card } from "@/components/ui/card";

type Props = {
  intelligence: HouseholdIntelligenceResponse;
};

export function HouseholdRecommendedActionsCard({
  intelligence,
}: Props) {
  const actions = buildRecommendedActions(intelligence);

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-indigo-500/15 p-3 text-indigo-400">
          <Sparkles size={21} />
        </div>

        <div>
          <h2 className="text-xl font-semibold text-white">
            Suggested Actions
          </h2>

          <p className="text-sm text-slate-500">
            Practical household care next steps
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {actions.map((action, index) => (
          <div
            key={`${action.title}-${index}`}
            className="rounded-2xl border border-slate-800 bg-slate-950/50 p-5"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 shrink-0 rounded-lg bg-indigo-500/15 p-2 text-indigo-400">
                <HeartHandshake size={18} />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <p className="font-semibold text-white">
                    {action.title}
                  </p>

                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getPriorityClass(
                      action.priority
                    )}`}
                  >
                    {action.priority}
                  </span>
                </div>

                <p className="mt-3 text-sm leading-6 text-slate-300">
                  {action.description}
                </p>

                <div className="mt-4 flex items-center gap-2 text-sm font-medium text-indigo-300">
                  <ArrowRight size={16} />
                  {action.nextStep}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

type RecommendedAction = {
  title: string;
  description: string;
  nextStep: string;
  priority: "LOW" | "MEDIUM" | "HIGH";
};

function buildRecommendedActions(
  intelligence: HouseholdIntelligenceResponse
): RecommendedAction[] {
  const actions: RecommendedAction[] = [];

  if (intelligence.recommended_action) {
    actions.push({
      title: "Household pastoral response",
      description: intelligence.recommended_action,
      nextStep: "Review and coordinate the next care step",
      priority: getPrimaryPriority(intelligence.risk_level),
    });
  }

  intelligence.insights.forEach((insight) => {
    if (!insight.recommendation) {
      return;
    }

    actions.push({
      title: insight.title,
      description: insight.recommendation,
      nextStep: "Assign this recommendation to a ministry leader",
      priority: getInsightPriority(insight.severity),
    });
  });

  if (actions.length === 0) {
    return [
      {
        title: "Continue normal pastoral awareness",
        description:
          "No immediate household intervention is recommended based on the information currently available.",
        nextStep: "Continue monitoring attendance and care activity",
        priority: "LOW",
      },
    ];
  }

  return removeDuplicateActions(actions);
}

function removeDuplicateActions(
  actions: RecommendedAction[]
): RecommendedAction[] {
  const seen = new Set<string>();

  return actions.filter((action) => {
    const key = action.description.trim().toLowerCase();

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function getPrimaryPriority(
  riskLevel: string
): RecommendedAction["priority"] {
  switch (riskLevel.toUpperCase()) {
    case "CRITICAL":
    case "AT_RISK":
      return "HIGH";

    case "WATCH":
      return "MEDIUM";

    default:
      return "LOW";
  }
}

function getInsightPriority(
  severity: string
): RecommendedAction["priority"] {
  const normalized = severity.toUpperCase();

  if (["HIGH", "CRITICAL", "AT_RISK"].includes(normalized)) {
    return "HIGH";
  }

  if (["MEDIUM", "WATCH", "WARNING"].includes(normalized)) {
    return "MEDIUM";
  }

  return "LOW";
}

function getPriorityClass(priority: RecommendedAction["priority"]) {
  switch (priority) {
    case "HIGH":
      return "bg-rose-500/15 text-rose-300";

    case "MEDIUM":
      return "bg-amber-500/15 text-amber-300";

    default:
      return "bg-emerald-500/15 text-emerald-300";
  }
}